import { StorageAuth } from "./../localStorage/StorageAuth";
import { SWRResponse } from "swr";
import { ApiSchemaAppointment } from "../../models/api";
import { AxiosAuth } from "../axios";
import {
  useExtendedApiAccess,
  CreateAccessObject,
} from "./useExtendedApiAccess";
import { AppRouteApi } from "../../config/appRoute";
import { PaginatedData } from "../../models/api";
import { useAppSelector } from "../redux";
import { useRef, useEffect } from "react";
import {
  ApiResponseContainer,
  ApiSchemaAppointmentDetails,
} from "../../models/api";
import { useAppDispatch } from "../redux/ReduxStore";
import { ActionsAppointmentTable } from "../redux/slices/sliceAppointmentTable";
import { showErrorToast } from "../../components/toast.component";
import {
  ApiSchemaAppointmentSimple,
  ApiSchemaAppointmentByMonth,
  ApiSchemaAppointmentById,
  ApiSchemaAppointmentIdByPatient,
  ApiSchemaActivityByAppointmentIdAndDate,
} from "../../models/api/appointment";
import { appointmentCalendarActions } from "../redux/slices/sliceAppointmentCalendar";
import { ApiSchemaHospitalisationType } from "../../models/api/hospitalisationType";

const CacheIds = {
  GetAll: AppRouteApi.Appointment.All(),
  Filter: AppRouteApi.Appointment.Filter(),
  ThisMonth: AppRouteApi.Appointment.ThisMonth(),
  ByDateRange: AppRouteApi.Appointment.ByDateRangeV2(),
  ByDate: AppRouteApi.Appointment.ByDate(),
  GetHospitalisationTypes: AppRouteApi.Appointment.GetHospitalisationTypes(),
};

function useApiAppointmentGetAll(): SWRResponse<
  ApiSchemaAppointment[] | undefined,
  Error
> {
  const { CompanyId } = StorageAuth;
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const api = useExtendedApiAccess<ApiSchemaAppointment[]>(
    CacheIds.GetAll,
    () =>
      AxiosAuth.get(CacheIds.GetAll, {
        params: {
          CompanyId,
        },
      })
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      (async () => {
        dispatch(ActionsAppointmentTable.setIsLoading(true));
        const res = await AxiosAuth.get<
          ApiResponseContainer<ApiSchemaAppointment[]>
        >(CacheIds.GetAll, {
          params: {
            CompanyId,
          },
        });

        if (res.data.isSuccess) {
          api.mutate(res.data.data, false);
        }
        dispatch(ActionsAppointmentTable.setIsLoading(false));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CompanyId]);

  return api;
}

function useApiAppointmentFilter() {
  const api = useExtendedApiAccess<
    ApiResponseContainer<ApiSchemaHospitalisationType>
  >(CacheIds.GetHospitalisationTypes, () =>
    AxiosAuth.get(CacheIds.GetHospitalisationTypes)
  );

  return api;
}

function useApiAppointmentThisMonth(
  companyIdParam?: string
): SWRResponse<ApiSchemaAppointmentByMonth[] | undefined, Error> {
  const {
    filterNurse: caregiverSearchKey,
    filterPatient: patientSearchKey,
    searchKey,
  } = useAppSelector((state) => state.appointmentCalendar);
  const { CompanyId: companyId } = StorageAuth;
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const api = useExtendedApiAccess<ApiSchemaAppointmentByMonth[]>(
    CacheIds.ThisMonth,
    () =>
      AxiosAuth.post(CacheIds.ThisMonth, {
        patientSearchKey,
        caregiverSearchKey,
        companyId: companyIdParam ?? companyId,
        searchKey,
      })
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      (async () => {
        dispatch(appointmentCalendarActions.setIsLoading(true));
        const res = await AxiosAuth.post<
          ApiResponseContainer<ApiSchemaAppointmentByMonth[]>
        >(CacheIds.ThisMonth, {
          patientSearchKey,
          caregiverSearchKey,
          companyId: companyIdParam ?? companyId,
          searchKey,
        });

        if (res.data.isSuccess) {
          api.mutate(res.data.data, false);
        }
        dispatch(appointmentCalendarActions.setIsLoading(false));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caregiverSearchKey, companyId, patientSearchKey, searchKey]);

  return api;
}

function useApiAppointmentByDateRange(
  companyIdParam?: string
): SWRResponse<ApiSchemaAppointmentByMonth[] | undefined, Error> {
  const {
    filterNurse: caregiverSearchKey,
    filterPatient: patientSearchKey,
    searchKey,
    monthStart,
    monthEnd,
  } = useAppSelector((state) => state.appointmentCalendar);
  const { CompanyId: companyId } = StorageAuth;
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const api = useExtendedApiAccess<ApiSchemaAppointmentByMonth[]>(
    CacheIds.ByDateRange,
    () =>
      AxiosAuth.post(CacheIds.ByDateRange, {
        patientSearchKey,
        caregiverSearchKey,
        companyId: companyIdParam ?? companyId,
        searchKey,
        fromDate: monthStart,
        toDate: monthEnd,
      })
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      (async () => {
        dispatch(appointmentCalendarActions.setIsLoading(true));
        const res = await AxiosAuth.post<
          ApiResponseContainer<ApiSchemaAppointmentByMonth[]>
        >(CacheIds.ByDateRange, {
          patientSearchKey,
          caregiverSearchKey,
          companyId: companyIdParam ?? companyId,
          searchKey,
          fromDate: monthStart,
          toDate: monthEnd,
        });

        if (res.data.isSuccess) {
          api.mutate(res.data.data, false);
        }
        dispatch(appointmentCalendarActions.setIsLoading(false));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    caregiverSearchKey,
    companyId,
    patientSearchKey,
    searchKey,
    monthStart,
    monthEnd,
  ]);

  return api;
}

function useGetHospitalisationTypes(): SWRResponse<
  ApiSchemaHospitalisationType[] | undefined,
  Error
> {
  const api = useExtendedApiAccess<ApiSchemaHospitalisationType[]>(
    CacheIds.GetHospitalisationTypes,
    () => AxiosAuth.get(CacheIds.GetHospitalisationTypes)
  );

  return api;
}

const AccessObject = {
  GetAll: CreateAccessObject<
    ApiSchemaAppointment[],
    typeof useApiAppointmentGetAll
  >(CacheIds.GetAll, useApiAppointmentGetAll),

  Filter: CreateAccessObject<
    PaginatedData<ApiSchemaAppointment>,
    typeof useApiAppointmentFilter
  >(CacheIds.Filter, useApiAppointmentFilter),

  ThisMonth: CreateAccessObject<
    ApiSchemaAppointmentByMonth[],
    typeof useApiAppointmentThisMonth
  >(CacheIds.ThisMonth, useApiAppointmentThisMonth),

  ByDateRange: CreateAccessObject<
    ApiSchemaAppointmentSimple[],
    typeof useApiAppointmentByDateRange
  >(CacheIds.ByDateRange, useApiAppointmentByDateRange),

  GetHospitalisationTypes: CreateAccessObject<
    ApiSchemaHospitalisationType[],
    typeof useGetHospitalisationTypes
  >(CacheIds.GetHospitalisationTypes, useGetHospitalisationTypes),
};

async function getAppointmentByDate(
  selectedDate: string,
  companyIdParam?: string
): Promise<ApiSchemaAppointmentDetails[] | undefined> {
  const { CompanyId: companyId } = StorageAuth;

  const api = await AxiosAuth.post<
    ApiResponseContainer<ApiSchemaAppointmentDetails[]>
  >(CacheIds.ByDate, {
    companyId: companyIdParam ?? companyId,
    date: selectedDate,
  });

  return api.data.data;
}

async function GetActivityByAppointmentIdAndDate(
  appointmentId: string,
  date: string
): Promise<ApiSchemaActivityByAppointmentIdAndDate | null | undefined> {
  try {
    const api = await AxiosAuth.get<
      ApiResponseContainer<ApiSchemaActivityByAppointmentIdAndDate | null>
    >(AppRouteApi.Appointment.GetActivityByAppointmentIdAndDate(), {
      params: { AppointmentId: appointmentId, Date: date },
    });
    return api.data.data;
  } catch (error) {
    console.log("ERROR: ", error)
    return null;
  }
}

async function CheckInForce(
  appointmentId: string,
  date: string
) {
  try {
    const api = await AxiosAuth.post<ApiResponseContainer<unknown>>(
      AppRouteApi.Appointment.CheckInForce(),
      {
        appointmentId, date
      }
    );
    return api.data;
  } catch (error: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(error.message).data });
  }
}

async function CheckOutForce(
  appointmentId: string,
  date: string
) {
  try {
    const api = await AxiosAuth.post<ApiResponseContainer<unknown>>(
      AppRouteApi.Appointment.CheckOutForce(),
      {
        appointmentId, date
      }
    );
    return api.data;
  } catch (error: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(error.message).data });
  }
}

async function UpdateCheckInTime(
  appointmentId: string,
  previousDateTime: string,
  updatedDateTime: string
) {
  try {
    const api = await AxiosAuth.put<ApiResponseContainer<unknown>>(
      AppRouteApi.Appointment.CheckIn(),
      { appointmentId, previousDateTime, updatedDateTime }
    );
    return api.data;
  } catch (error: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(error.message).data });
  }
}

async function UpdateCheckOutTime(
  appointmentId: string,
  previousDateTime: string,
  updatedDateTime: string
) {
  try {
    const api = await AxiosAuth.put<ApiResponseContainer<unknown>>(
      AppRouteApi.Appointment.CheckOut(),
      { appointmentId, previousDateTime, updatedDateTime }
    );
    return api.data;
  } catch (error: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(error.message).data });
  }
}

async function AddAppointment(
  appointment: FormData
): Promise<ApiResponseContainer<ApiSchemaAppointment>> {
  try {
    const response = await AxiosAuth.post<
      ApiResponseContainer<ApiSchemaAppointment>
    >(AppRouteApi.Appointment.Root(), appointment);
    if (response.data.isSuccess) {
      const updAction = (
        res?: ApiSchemaAppointment[]
      ): ApiSchemaAppointment[] | undefined => {
        if (res) return res;
      };
      AccessObject.GetAll.Mutate(updAction());
    }
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

async function UpdateAppointment(
  appointment: FormData
): Promise<ApiResponseContainer<ApiSchemaAppointment>> {
  try {
    const response = await AxiosAuth.put<
      ApiResponseContainer<ApiSchemaAppointment>
    >(AppRouteApi.Appointment.Root(), appointment);
    if (response.data.isSuccess) {
      const updAction = (
        res?: ApiSchemaAppointment[]
      ): ApiSchemaAppointment[] | undefined => {
        if (res) return res;
      };
      AccessObject.GetAll.Mutate(updAction());
      return response.data;
    }
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

async function DeleteAppointment(id: string): Promise<boolean> {
  try {
    const response = await AxiosAuth.delete<ApiResponseContainer<boolean>>(
      AppRouteApi.Appointment.Delete(id)
    );
    if (response.data.isSuccess) return true;
  } catch (e: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return false;
}

async function GetAppointmentById(
  id: string
): Promise<ApiSchemaAppointmentById | undefined> {
  try {
    const response = await AxiosAuth.get<
      ApiResponseContainer<ApiSchemaAppointmentById>
    >(AppRouteApi.Appointment.ById(id));
    if (response.data.isSuccess) return response.data.data;
  } catch (e: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

async function GetAppointmentAssignments(
  caregiverId: string,
  companyId?: string
): Promise<
  | { unassigned: ApiSchemaAppointment[]; assigned: ApiSchemaAppointment[] }
  | undefined
> {
  try {
    const response = await AxiosAuth.get<
      ApiResponseContainer<{
        unassigned: ApiSchemaAppointment[];
        assigned: ApiSchemaAppointment[];
      }>
    >(AppRouteApi.Appointment.AppointmentAssignments(), {
      params: { companyId: companyId || StorageAuth.CompanyId, caregiverId },
    });
    if (response.data.isSuccess) return response.data.data;
  } catch (e: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

async function ManageCaregiverAssignments(data: {
  caregiverId: string;
  added: string[];
  removed: string[];
}): Promise<boolean> {
  try {
    const response = await AxiosAuth.post<ApiResponseContainer<boolean>>(
      AppRouteApi.Appointment.ManageCaregiverAssignments(),
      data
    );
    if (response.data.isSuccess) return !!response.data.data;
  } catch (e: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
  return false;
}

async function ChangeAppointmentStatus(
  appointment: ApiSchemaAppointment & {
    sl: number;
    checked?: boolean;
  },
  ChangedStatus: string,
  SelectedCaregiverSkey?: string
) {
  let body = {
    appointmentId: appointment.sKey,
    status: ChangedStatus,
    careGiverSKey: SelectedCaregiverSkey,
  };

  try {
    const response = await AxiosAuth.patch<
      ApiResponseContainer<ApiSchemaAppointment>
    >(AppRouteApi.Appointment.ChangeStatus(), body);
    if (response.data.isSuccess) {
      const updAction = (
        res?: ApiSchemaAppointment[]
      ): ApiSchemaAppointment[] | undefined => {
        if (res) return res;
      };
      AccessObject.GetAll.Mutate(updAction());
    }
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

async function GetAppointmentIdByPatient(patientSKey: string, appointmentId: string | undefined) {
  try {
    const response = await AxiosAuth.get<
      ApiResponseContainer<ApiSchemaAppointmentIdByPatient[]>
    >(AppRouteApi.Appointment.GetAppointmentIdByPatient(patientSKey, appointmentId));
    return response.data;
  } catch (error: any) {
    return showErrorToast({
      isSuccess: false,
      ...JSON.parse(error.message).data,
    });
  }
}

export const ApiAppointment = {
  GetAll: AccessObject.GetAll,
  Filter: AccessObject.Filter,
  ThisMonth: AccessObject.ThisMonth,
  ByDateRange: AccessObject.ByDateRange,
  GetHospitalisationTypes: AccessObject.GetHospitalisationTypes,
  ByDate: getAppointmentByDate,
  Add: AddAppointment,
  Delete: DeleteAppointment,
  Update: UpdateAppointment,
  GetById: GetAppointmentById,
  GetAppointmentAssignments,
  ManageCaregiverAssignments,
  ChangeAppointmentStatus,
  GetAppointmentIdByPatient,
  GetActivityByAppointmentIdAndDate,
  CheckInForce,
  CheckOutForce,
  UpdateCheckInTime,
  UpdateCheckOutTime,
};
